// Query analytics and alerting (`06` §3.13, `SC-F808`–`SC-F819`).
//
// **Analytics is derived from audit records, not from metrics**, and that is a
// design decision rather than an implementation shortcut. Sluice's Prometheus
// catalogue has no query-latency histogram, no per-query REST counter, no reload
// counter, no per-datasource health gauge and no budget gauge — while an audit
// record carries the subject, the tables, the catalogs, the applied policies, the
// decision, the error code, the duration, the row count and the truncation flag.
// That is why [getInstanceMetrics] is v1 and blocked on `SLUICE-U07`, and
// analytics is MVP. `SC-S808`'s last criterion is that the UI **says** this.
//
// The three read routes are served by `internal/analytics/dashboard`, and the
// four shape rules that matter to a chart are all its:
//
//   - **Every bucket in the range is present, zero-filled.** A client must never
//     have to distinguish "no traffic" from "omitted key" (`SC-S808`).
//   - **Points are `[timestamp, value]` pairs**, so a 2000-bucket series stays
//     compact on the wire.
//   - **Every response states which tier answered it** in `source`. `daily` means
//     the analytics worker was unreachable and the answer came from the
//     `analytics_daily` rollup — daily grain, decision-only grouping, top-N lists
//     already capped when they were stored, and `duration_p99` silently standing
//     in for the stored *maximum*. Every one of those limits is reported on the
//     response rather than inferred, and a UI that renders coarse data as if it
//     were fine-grained is the failure the field exists to prevent.
//   - **`ingest` is mandatory**, for the same reason it is on audit: a user must
//     never mistake ingest lag for a drop in traffic.
//
// One wire quirk is worth stating rather than hiding: `analytics.Watermark`
// carries **no JSON tags** in Go, so it serialises with Go field names
// (`AsOf`, `LagSeconds`, `Complete`, `Stale`, `Profile`) while every other field
// on these responses is snake_case. [readWatermark] normalises both spellings so
// no page has to know, and so a server-side fix is not a UI break.

import { del, getJSON, newIdempotencyKey, query, sendJSON, type Page } from "./http.js";
import type { AuditDecision, AuditFilter, AuditProfileName } from "./audit.js";

const instBase = (instanceId: string) => `/v1/instances/${encodeURIComponent(instanceId)}`;
const orgBase = (orgId: string) => `/v1/orgs/${encodeURIComponent(orgId)}`;

/** AnalyticsMetric is the closed metric vocabulary (`analytics.Metrics`). */
export type AnalyticsMetric =
  | "queries"
  | "denied"
  | "rejected"
  | "errors"
  | "rows_returned"
  | "truncated"
  | "duration_p50"
  | "duration_p95"
  | "duration_p99";

/** AnalyticsInterval is the bucket width. The five widths span 5 minutes to a
 * day, and an **absent** `interval` is derived server-side as the finest width
 * that fits under the 2000-bucket cap — which is what a chart wants. */
export type AnalyticsInterval = "5m" | "15m" | "1h" | "6h" | "1d";

/** ANALYTICS_INTERVALS is the vocabulary coarsest-last, matching
 * `analytics.Intervals`. */
export const ANALYTICS_INTERVALS: readonly AnalyticsInterval[] = ["5m", "15m", "1h", "6h", "1d"];

/** MAX_BUCKETS is `analytics.MaxBuckets`: a window/interval pair yielding more is
 * refused, not silently coarsened. */
export const MAX_BUCKETS = 2000;

/** AnalyticsDimension is the breakdown key (`analytics.Dimensions`). */
export type AnalyticsDimension =
  | "decision"
  | "error_code"
  | "subject"
  | "catalog"
  | "table"
  | "policy";

/**
 * AnalyticsSource names which tier answered, and it is on every response.
 *
 * `04` §6.3 serves ranges of a week or more from the rollups and shorter ones
 * from the detail tier, and a reader comparing two charts needs to know which
 * they are looking at: a rollup-sourced percentile is re-derived from 13 histogram
 * buckets with linear interpolation, which is exact enough for a trend and not
 * exact enough to quote to three significant figures.
 */
export type AnalyticsSource = "detail" | "rollup" | "daily" | "empty";

/**
 * AnalyticsWatermark is the ingest-completeness statement every analytics
 * response carries (`SC-S806`).
 *
 * `complete` is false when the requested window extends past `as_of`, and that is
 * the signal a chart uses to **mark the trailing region as incomplete** instead of
 * drawing it as zero. `lag_seconds` is a number rather than something the client
 * computes, so a clock-skewed browser cannot render a negative lag.
 */
export interface AnalyticsWatermark {
  /** Null when nothing has been ingested for the scope. */
  as_of: string | null;
  lag_seconds: number;
  complete: boolean;
  stale: boolean;
  /** Empty when the scope spans instances with different profiles. */
  profile: AuditProfileName | "";
}

/** Go's zero `time.Time`, which is what an unset `AsOf` marshals to. It means
 * "nothing ingested", not "the year 1". */
const GO_ZERO_TIME = "0001-01-01T00:00:00Z";

/**
 * readWatermark normalises the ingest block off any analytics response.
 *
 * It accepts both the Go field names the handler currently emits and the
 * snake_case spelling the rest of the surface uses, so this module is the only
 * place that has to know about the discrepancy — see the module header. A missing
 * block yields a watermark that reports itself incomplete with a null `as_of`,
 * which is the safe direction: the UI then says "freshness unknown" rather than
 * implying the window is fully ingested.
 */
export function readWatermark(raw: unknown): AnalyticsWatermark {
  const o = (raw ?? {}) as Record<string, unknown>;
  const pick = <T>(...keys: string[]): T | undefined => {
    for (const k of keys) if (o[k] !== undefined && o[k] !== null) return o[k] as T;
    return undefined;
  };
  const asOfRaw = pick<string>("as_of", "AsOf");
  const asOf = !asOfRaw || asOfRaw.startsWith("0001-01-01") || asOfRaw === GO_ZERO_TIME ? null : asOfRaw;
  return {
    as_of: asOf,
    lag_seconds: Number(pick<number>("lag_seconds", "LagSeconds") ?? 0),
    complete: Boolean(pick<boolean>("complete", "Complete") ?? false),
    stale: Boolean(pick<boolean>("stale", "Stale") ?? false),
    profile: (pick<string>("profile", "Profile") ?? "") as AuditProfileName | "",
  };
}

/** TimeseriesPoint is `[RFC 3339 timestamp, value]`. */
export type TimeseriesPoint = [string, number];

/** TimeseriesSeries is one grouped line. `key` has at most one entry — the
 * `group_by` dimension and its value — and is empty on an ungrouped series. */
export interface TimeseriesSeries {
  key: Partial<Record<AnalyticsDimension, string>>;
  points: TimeseriesPoint[];
}

/**
 * Timeseries is `GET .../analytics/timeseries` (`SC-F808`).
 *
 * `interval` is what the server **actually bucketed at**, not what was asked
 * for: the daily fallback serves a daily series whatever was requested and
 * reports `1d` here, because a response claiming hourly resolution over daily
 * data would make a chart lie about its own x-axis. Label the axis from this
 * field.
 */
export interface Timeseries {
  metric: AnalyticsMetric;
  interval: AnalyticsInterval;
  from: string;
  to: string;
  series: TimeseriesSeries[];
  source: AnalyticsSource;
  ingest: AnalyticsWatermark;
}

/**
 * AnalyticsFilter is the subset of the audit allowlist the analytical tier can
 * apply (`analytics.Filter`).
 *
 * Three audit filters are **not** here, and each absence is honest:
 *
 *   - **`q` (SQL text)** is refused with a `422` naming the reason: the analytical
 *     tier stores no query text, so accepting the parameter and ignoring it would
 *     be a filter that silently did nothing.
 *   - **`event_type`, `schema`, `origin`, `group`** have no column here. The
 *     server ignores unknown keys rather than refusing them, which is exactly why
 *     [toAnalyticsFilter] reports what it dropped instead of passing them and
 *     letting a reader believe they applied.
 *   - **`remote_ip`** holds the edge proxy's address on every record
 *     (`SLUICE-U12`), so a filter over it matches everything or nothing.
 */
export interface AnalyticsFilter {
  subject?: string[];
  issuer?: string[];
  decision?: AuditDecision[];
  error_code?: string[];
  catalog?: string[];
  table?: string[];
  policy?: string[];
  query_id?: string[];
  truncated?: boolean;
  min_duration_ms?: number;
}

/** ANALYTICS_FILTER_KEYS is [AnalyticsFilter]'s key set as data, so
 * [toAnalyticsFilter] cannot drift from the type. */
const ANALYTICS_FILTER_KEYS = [
  "subject",
  "issuer",
  "decision",
  "error_code",
  "catalog",
  "table",
  "policy",
  "query_id",
  "truncated",
  "min_duration_ms",
] as const;

/**
 * toAnalyticsFilter narrows an audit filter to what the analytical tier can
 * honour, and **names what it had to drop**.
 *
 * One filter bar drives the explorer and the dashboard, which is the point of the
 * shared allowlist — but the two tiers do not carry the same columns. Silently
 * discarding the difference would answer a chart about a wider slice than the
 * filter bar claims, so the caller renders `dropped` next to the charts.
 */
export function toAnalyticsFilter(filter: AuditFilter): {
  filter: AnalyticsFilter;
  dropped: string[];
} {
  const out: Record<string, unknown> = {};
  const dropped: string[] = [];
  const analytical = new Set<string>(ANALYTICS_FILTER_KEYS);
  // `from`, `to`, `sort`, `limit` and `cursor` are the window and the paging, not
  // predicates, so they are neither copied nor reported as dropped.
  const notAPredicate = new Set(["from", "to", "sort", "limit", "cursor"]);
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) continue;
    if (notAPredicate.has(key)) continue;
    if (analytical.has(key)) out[key] = value;
    else dropped.push(key);
  }
  return { filter: out as AnalyticsFilter, dropped };
}

/** TimeseriesParams is the query. An absent `interval` lets the server derive
 * the finest width that fits the window. */
export interface TimeseriesParams {
  metric: AnalyticsMetric;
  /** Inclusive; RFC 3339 with an offset, or `YYYY-MM-DD` read as midnight UTC. */
  from: string;
  /** Exclusive. Half-open makes adjacent windows tile without double-counting. */
  to: string;
  interval?: AnalyticsInterval;
  group_by?: AnalyticsDimension;
  filter?: AnalyticsFilter;
  signal?: AbortSignal;
}

/** getTimeseries reads a bucketed series for one instance. */
export async function getTimeseries(
  instanceId: string,
  params: TimeseriesParams,
): Promise<Timeseries> {
  const { filter, signal, ...rest } = params;
  const res = await getJSON<Timeseries>(
    `${instBase(instanceId)}/analytics/timeseries${query({ ...rest, ...(filter ?? {}) })}`,
    { signal },
  );
  return { ...res, ingest: readWatermark(res.ingest) };
}

/** BreakdownItem is one ranked entry. `denied` is what turns a "top tables" list
 * into an access-review starting point rather than a traffic report. */
export interface BreakdownItem {
  /** The dimension's value: a decision, an error code, a subject id, a catalog, a
   * fully qualified table name, or a policy name. */
  value: string;
  count: number;
  denied: number;
}

/**
 * Breakdown is `GET .../analytics/breakdown` — a top-N by one dimension.
 *
 * Three fields exist so a reader is never misled by the ranking:
 *
 *   - `unnested` says the dimension is an array column, so the counts legitimately
 *     sum to more than the query total. Without it, a reader adding up a "top
 *     tables" column concludes one of the two numbers is wrong.
 *   - `truncated` turns "top 20" into "top 20 of many", and the daily fallback
 *     sets it **always** — its source lists were already capped per day before
 *     they were stored, so "top 5 of the top 20 of each day" is not the top 5 of
 *     the window.
 *   - `distinct` is what lets the label read "top 20 of 10,431".
 */
export interface Breakdown {
  dimension: AnalyticsDimension;
  from: string;
  to: string;
  items: BreakdownItem[];
  unnested: boolean;
  truncated: boolean;
  distinct: number;
  source: AnalyticsSource;
  ingest: AnalyticsWatermark;
}

/** MAX_BREAKDOWN_LIMIT is `analytics.MaxBreakdownLimit`: a ranked list is
 * something a human reads, and the only use for "every distinct subject" is an
 * export — which is `SC-F805`'s job and is rate-limited precisely so a bulk read
 * cannot become an exfiltration primitive. */
export const MAX_BREAKDOWN_LIMIT = 500;

/** BreakdownParams is the query. `dimension` is the parameter name — not
 * `group_by`, which is the timeseries route's spelling. */
export interface BreakdownParams {
  dimension: AnalyticsDimension;
  from: string;
  to: string;
  limit?: number;
  filter?: AnalyticsFilter;
  signal?: AbortSignal;
}

/**
 * getBreakdown reads a top-N by a dimension.
 *
 * Under `source: "daily"` only `decision`, `table` and `subject` are expressible:
 * `error_code`, `catalog` and `policy` have no column in the rollup and come back
 * as an **empty item list with the tier named** rather than as an error, so the
 * dashboard's other panels still render. A panel that says "this needs the
 * analytics worker" is more useful than a 503 that takes the page down — but an
 * empty list under `daily` must never be rendered as "nothing happened".
 */
export async function getBreakdown(
  instanceId: string,
  params: BreakdownParams,
): Promise<Breakdown> {
  const { filter, signal, ...rest } = params;
  const res = await getJSON<Breakdown>(
    `${instBase(instanceId)}/analytics/breakdown${query({ ...rest, ...(filter ?? {}) })}`,
    { signal },
  );
  return { ...res, ingest: readWatermark(res.ingest), items: res.items ?? [] };
}

/**
 * Count is `GET .../analytics/count` (`OD-30`).
 *
 * The route exists so that "how many denials in July" has an honest answer
 * without weakening the audit listing's contract, which refuses a `total` on
 * single-write-connection grounds. Its whole value is in `approximate` being
 * trustworthy: the handler never clears the flag, and `basis` says in words why it
 * is set — a pre-aggregated tier, a filter the tier could not apply, or a window
 * extending past the ingest watermark. **Render `basis` whenever `approximate`
 * is true.**
 */
export interface Count {
  count: number;
  approximate: boolean;
  basis?: string;
  from: string;
  to: string;
  source: AnalyticsSource;
  ingest: AnalyticsWatermark;
}

/** getCount reads an explicitly-approximate aggregate for a window. */
export async function getCount(
  instanceId: string,
  params: { from: string; to: string; filter?: AnalyticsFilter; signal?: AbortSignal },
): Promise<Count> {
  const { filter, signal, ...rest } = params;
  const res = await getJSON<Count>(
    `${instBase(instanceId)}/analytics/count${query({ ...rest, ...(filter ?? {}) })}`,
    { signal },
  );
  return { ...res, ingest: readWatermark(res.ingest) };
}

/**
 * getOrgTimeseries reads the organization rollup across instances (v1).
 *
 * TODO(SC-F808): `internal/analytics/dashboard` registers the three
 * instance-scoped routes only, so this is `06` §3.13's shape and not a
 * transcription of a handler that exists. The scope widening is a partition-prefix
 * change server-side, not a new query.
 */
export async function getOrgTimeseries(
  orgId: string,
  params: TimeseriesParams,
): Promise<Timeseries> {
  const { filter, signal, ...rest } = params;
  const res = await getJSON<Timeseries>(
    `${orgBase(orgId)}/analytics/timeseries${query({ ...rest, ...(filter ?? {}) })}`,
    { signal },
  );
  return { ...res, ingest: readWatermark(res.ingest) };
}

// ──────────────────────────────────────── instance metrics (`SC-F813`) ──

/** MetricSample is one series: a name, its labels and its value.
 *
 * `value` is a **string** and that is not a serialisation accident. A histogram's
 * `+Inf` bucket and an unobserved summary quantile are legitimately `+Inf` and
 * `NaN`, neither of which JSON can represent as a number — so formatting them
 * server-side is what stops one legitimate sample failing the whole response. A
 * renderer that needs arithmetic parses it and handles the three non-finite
 * spellings. */
export interface MetricSample {
  name: string;
  labels?: Record<string, string>;
  value: string;
}

/** MetricRow is one catalogue entry joined to what the instance reported. */
export interface MetricRow {
  name: string;
  type: "counter" | "gauge" | "histogram" | "summary" | "unknown";
  labels?: string[];
  /** What an operator learns from this series. */
  answers: string;
  /** True when ingested audit records answer the same question with more
   * dimensions — in which case the analytics dashboard is the better source and
   * the UI should say so rather than let the metric compete with it. */
  also_in_audit: boolean;
  /** False when the exposition did not carry the family. **This is an ordinary
   * state, not a gap:** a collector that has never observed an event emits no
   * series, so a missing `sluice_audit_dropped_total` is the good case. Render it
   * as "not yet observed". */
  present: boolean;
  help?: string;
  samples?: MetricSample[];
  samples_truncated: boolean;
}

/** AbsentSignal is a question the Prometheus catalogue structurally cannot
 * answer, with the surface that answers it instead.
 *
 * There are five and they are the reason this page is thin: `04` §1.3 calls them
 * "the gaps that determine the analytics design". Every one has a real answer
 * elsewhere, so the absence is a routing fact rather than a missing capability —
 * and a page that showed eighteen series and stayed silent about query latency
 * would invite the reader to conclude the gateway has none. */
export interface AbsentSignal {
  signal: string;
  why: string;
  answered_by: string;
  feature: string;
}

/** MetricsPosture is how the reading was obtained and what is deliberately not
 * built. Every sentence comes from the server so one disclosure is not
 * maintained in two places. */
export interface MetricsPosture {
  transport: string;
  credential: string;
  no_scrape_job: string;
  /** The option refused outright: a shared metrics token across the fleet. It is
   * on the payload rather than in a footnote because it is the fallback an
   * operator reaches for by default. */
  rejected: string;
  upstream: string;
  not_audit: string;
}

/** InstanceMetrics is `GET /v1/instances/{id}/metrics` (`SC-F813`).
 *
 * **This is the gateway's own process metrics and never the source of the query
 * analytics above.** There is no scrape pipeline behind it: `/metrics` sits on
 * Sluice's admin listener behind the per-instance admin bearer token and
 * Prometheus sets credentials per scrape *job*, so no fleet-wide job can
 * authenticate (`SC-F812`, `SLUICE-U07`). The control plane reads one instance,
 * once, per call — no collector, no retention, no series history, and the values
 * are counters since the process started.
 *
 * `catalogue` is always the full nineteen rows in the same order, whether or not
 * the instance answered, and an unreachable instance answers `200` with
 * `available: false`: the catalogue, the absent signals and the posture are facts
 * about the pinned build and about this control plane, and blanking them would
 * remove the part of the page that explains why the numbers are missing. */
export interface InstanceMetrics {
  instance: string;
  observed_at: string;
  available: boolean;
  error: { code: string; message: string } | null;
  catalogue: MetricRow[];
  /** `sluice_build_info`'s labels, flattened. Empty when the family was absent. */
  build?: Record<string, string>;
  /** Families the instance emitted that this control plane does not model — a
   * pin-bump signal (`SC-F323`), never an error. */
  unrecognized?: string[];
  absent_signals: AbsentSignal[];
  posture: MetricsPosture;
  exposition_bytes: number;
  truncated: boolean;
}

/**
 * getInstanceMetrics reads one instance's Prometheus catalogue (`SC-F813`).
 *
 * Present it as what it is — the gateway's own process metrics, read on demand —
 * and never as the source of the query analytics above, which it cannot be.
 */
export async function getInstanceMetrics(
  instanceId: string,
  signal?: AbortSignal,
): Promise<InstanceMetrics> {
  return getJSON<InstanceMetrics>(`${instBase(instanceId)}/metrics`, { signal });
}

// ────────────────────────────────────────────────── alerts (v1) ──

/**
 * AlertRule is an organization-level alert (`SC-F815`, v1).
 *
 * TODO(SC-F815): the condition shape is defined from `06` §3.13 and `04` §5; the
 * Go handler is v1 and does not exist yet. Notification targets are a selector
 * over the organization's webhook subscriptions, never a customer-supplied URL —
 * an arbitrary POST originating from the control-plane network is the strongest
 * SSRF primitive in the product (`07` T-03).
 */
export interface AlertRule {
  id: string;
  name: string;
  enabled: boolean;
  /** Empty = every instance in the organization. */
  instance: string | null;
  metric: AnalyticsMetric;
  /** Evaluation window, e.g. `15m`. */
  window: AnalyticsInterval;
  comparator: "gt" | "gte" | "lt" | "lte";
  threshold: number;
  filter?: AnalyticsFilter;
  /** Webhook subscription ids to notify. */
  notify: string[];
  created: string;
  updated: string;
}

/** Alert is one firing of a rule. */
export interface Alert {
  id: string;
  rule: string;
  rule_name: string;
  instance: string | null;
  state: "firing" | "resolved";
  value: number;
  threshold: number;
  fired_at: string;
  resolved_at: string | null;
}

/** listAlertRules returns the organization's alert rules (v1). */
export function listAlertRules(orgId: string): Promise<Page<AlertRule>> {
  return getJSON<Page<AlertRule>>(`${orgBase(orgId)}/alert-rules`);
}

/** createAlertRule adds a rule (v1). */
export function createAlertRule(
  orgId: string,
  input: Omit<AlertRule, "id" | "created" | "updated">,
): Promise<AlertRule> {
  return sendJSON<AlertRule>(`${orgBase(orgId)}/alert-rules`, "POST", input, {
    idempotencyKey: newIdempotencyKey(),
  });
}

/** patchAlertRule updates a rule (v1). */
export function patchAlertRule(
  orgId: string,
  ruleId: string,
  patch: Partial<AlertRule>,
): Promise<AlertRule> {
  return sendJSON<AlertRule>(
    `${orgBase(orgId)}/alert-rules/${encodeURIComponent(ruleId)}`,
    "PATCH",
    patch,
  );
}

/** deleteAlertRule removes a rule (v1). */
export function deleteAlertRule(orgId: string, ruleId: string): Promise<void> {
  return del(`${orgBase(orgId)}/alert-rules/${encodeURIComponent(ruleId)}`);
}

/** listAlerts returns fired alerts, newest first (v1). */
export function listAlerts(
  orgId: string,
  params: { state?: "firing" | "resolved"; limit?: number; cursor?: string } = {},
): Promise<Page<Alert>> {
  return getJSON<Page<Alert>>(`${orgBase(orgId)}/alerts${query({ ...params })}`);
}

// ─────────────────────────────── budget and rate-limit consumption (SC-F818) ──
//
// The two halves of this view have different provenances and the payload keeps them
// apart on purpose: `limits` is authored configuration and is exact, `subjects` is
// derived from ingested audit records and is fresh only to the ingest watermark, and
// `provenance` says which is which for every column. Render `provenance` — the whole
// feature rests on it, because the number an operator actually wants (the live budget
// counter in the instance's own SQLite) is not readable from the control plane at all.

/** LimitKeyKind is which of Sluice's two limit maps a key belongs to. `subject` is
 * `bySubject[spec.claims.subjectId]` and is consulted FIRST; `issuer` is
 * `byIssuer[spec.issuer]`. The binding NAME is in neither map. */
export type LimitKeyKind = "subject" | "issuer";

/** LimitKey is one map key a binding's limits are registered under. */
export interface LimitKey {
  kind: LimitKeyKind;
  value: string;
}

/** LimitFinding is one way an authored limit is silently enforced by nothing. */
export interface LimitFinding {
  severity: string;
  rule: string;
  name?: string;
  path?: string;
  message: string;
  title?: string;
  /** The Sluice source that makes the finding checkable rather than asserted. */
  upstream?: string;
}

/**
 * AuthoredLimit is one `SubjectBinding`'s declared rate limit and daily budget.
 *
 * `has_*` and `*_enforced` are deliberately separate: a `rateLimit` block with
 * `rps: 0` is authored and enforced by nothing — Sluice skips the binding when it
 * builds the map, so the subject falls through to the server config's
 * `default_subject_rps`, which may be HIGHER than the number on the form. A UI that
 * collapsed the two would hide the most common way a limit does nothing.
 */
export interface AuthoredLimit {
  binding: string;
  /** `jwt`, `api-key`, `both` or `none`. It decides whether `claims.subjectId` is a
   * claim path or a literal, which decides whether a per-subject key can match. */
  mode: string;
  keys: LimitKey[];
  rps?: number;
  burst?: number;
  cpu_seconds_per_day?: number;
  rows_per_day?: number;
  has_rate_limit: boolean;
  has_budget: boolean;
  rate_enforced: boolean;
  budget_enforced: boolean;
  findings?: LimitFinding[];
}

/** AuthoredLimits is the authored half of the consumption view. */
export interface AuthoredLimits {
  generation?: number;
  generation_state?: string;
  bindings: AuthoredLimit[];
  /** What applies to every subject no binding claims — and to every subject whose
   * binding declares `rps: 0`. */
  default_subject_rps: number;
  default_subject_burst: number;
  /** `budget.enabled` in the instance's server config. **`null` means unknown and
   * must not render as false**: telling a customer their budgets are off when the
   * answer was unavailable is the same class of error as telling them they are on. */
  budget_enabled: boolean | null;
  available: boolean;
  findings?: LimitFinding[];
}

/** SubjectConsumption is one observed subject's usage and the limit that governs it. */
export interface SubjectConsumption {
  subject: string;
  queries: number;
  denied: number;
  /** The audit-derived proxy for the rows dimension of the budget counter. */
  rows_served: number;
  /** The two AUDITED refusals — the only exact limit numbers on this view.
   * `rate_refusals` is the per-subject limiter only; the transport limiter refuses
   * before there is an identity to attribute and writes no audit record. */
  budget_refusals: number;
  rate_refusals: number;
  binding?: string;
  /** `subject`, `issuer` or absent. The two are not equally reliable: a subject key
   * is an exact string match, an issuer key covers everyone sharing that issuer. */
  matched_by?: LimitKeyKind;
  rows_per_day?: number;
  /** Present only when a rows budget governs AND the window is exactly one UTC day,
   * because the counter it approximates resets at midnight UTC. */
  rows_used_percent?: number;
}

/** Provenance is one statement of where a column comes from. `unobservable` rows are
 * numbers this build cannot produce and are present so the absence is a statement
 * rather than an empty column. */
export interface Provenance {
  field: string;
  source: "authored" | "audit" | "unobservable";
  statement: string;
  upstream?: string;
}

/** ConsumptionReport is `GET /v1/instances/{id}/analytics/consumption`. */
export interface ConsumptionReport {
  from: string;
  to: string;
  subjects: SubjectConsumption[];
  limits: AuthoredLimits;
  /** How many observed subjects no binding's limit claims. They are governed by the
   * server config's default rate and by no budget. */
  unclaimed: number;
  provenance: Provenance[];
  truncated?: boolean;
  source: AnalyticsSource;
  ingest: AnalyticsWatermark;
  /** Which observed columns could not be filled, one sentence each. Separate from
   * `provenance`: that says where a number comes from, this says why one is missing. */
  degraded?: string[];
}

/** getConsumption reads `SC-F818`'s report for one instance and window. */
export function getConsumption(
  instanceId: string,
  params: { from?: string; to?: string } = {},
): Promise<ConsumptionReport> {
  return getJSON<ConsumptionReport>(
    `/v1/instances/${encodeURIComponent(instanceId)}/analytics/consumption${query({ ...params })}`,
  );
}
